import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  SkeletonList,
  Textarea,
} from '@/components/ds';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  TicketThread,
  ThreadAttachment,
} from '@/components/feedback/TicketThread';
import { CapturedContextPanel } from '@/components/feedback/CapturedContextPanel';
import { statusMeta, TYPE_META } from '@/components/feedback/ticket-vocabulary';
import { useFormatters } from '@/hooks/useFormatters';
import { feedbackApi, isClosed, type TicketDetail } from '@/api/feedback.api';

/**
 * One report, in full.
 *
 * The screen the old design had nowhere for: the whole submission, what was
 * captured with it, the conversation, and — when it has been closed — the
 * team's answer and a one-click way to say it is not actually fixed.
 */
export function TicketDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { dateTime, relative } = useFormatters();

  const [data, setData] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);

  const ticketId = Number(id);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const detail = await feedbackApi.detail(ticketId);
      setData(detail);
      // Clearing the badge is a side effect of actually opening the thread,
      // not of the notification arriving.
      if (detail.ticket.unreadReplies > 0) {
        void feedbackApi.markRead(ticketId);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (Number.isFinite(ticketId)) load();
    else setNotFound(true);
  }, [load, ticketId]);

  const reply = async (body: string) => {
    await feedbackApi.reply(ticketId, body);
    await load();
  };

  const reopen = async () => {
    setReopening(true);
    try {
      await feedbackApi.reopen(ticketId, reopenReason.trim() || undefined);
      setReopenOpen(false);
      setReopenReason('');
      await load();
    } finally {
      setReopening(false);
    }
  };

  // The back arrow has to point the other way in Arabic; an arrow is direction,
  // not an icon, so it flips with the writing direction.
  const BackArrow = i18n.dir() === 'rtl' ? ArrowRight : ArrowLeft;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="mx-auto w-full max-w-3xl">
          <SkeletonList rows={4} />
        </div>
      </DashboardLayout>
    );
  }

  if (notFound || !data) {
    return (
      <DashboardLayout>
        <div className="mx-auto w-full max-w-3xl">
          <EmptyState
            title={t('feedback.detail.notFound')}
            action={
              <Button
                variant="secondary"
                onPress={() => navigate('/dashboard/feedback')}
              >
                {t('feedback.detail.back')}
              </Button>
            }
          />
        </div>
      </DashboardLayout>
    );
  }

  const { ticket, comments, attachments } = data;
  const meta = statusMeta(ticket.status);
  const typeMeta = TYPE_META[ticket.type];
  const closed = isClosed(ticket.status);
  const submissionAttachments = attachments.filter((f) => f.commentId === null);
  const details = ticket.submissionDetails;

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onPress={() => navigate('/dashboard/feedback')}
          startIcon={<BackArrow className="size-4" />}
        >
          {t('feedback.detail.back')}
        </Button>

        <Card>
          <Card.Body className="space-y-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={meta.tone}>{t(meta.labelKey)}</Badge>
                {typeMeta && <Badge>{t(typeMeta.labelKey)}</Badge>}
              </div>
              <h1 className="text-h1 text-foreground">{ticket.title}</h1>
              <p className="text-small text-muted">
                {ticket.reporter?.name
                  ? `${t('feedback.detail.reportedBy', { name: ticket.reporter.name })} · `
                  : ''}
                <time
                  dateTime={ticket.createdAt}
                  title={dateTime(ticket.createdAt)}
                >
                  {t('feedback.detail.reportedOn', {
                    when: relative(ticket.createdAt),
                  })}
                </time>
              </p>
            </div>

            {/* What the status actually means, in a sentence. A chip alone
                tells a dentist nothing about what happens next. */}
            <Alert tone={statusAlertTone(ticket.status)}>
              <span className="font-medium">
                {t('feedback.detail.whatNext')}:{' '}
              </span>
              {t(meta.meaningKey)}
            </Alert>

            {ticket.type === 'incorrect_data' && details && (
              <dl className="grid gap-3 rounded-lg bg-surface-1 p-3 sm:grid-cols-3">
                <div>
                  <dt className="text-micro text-muted">
                    {t('feedback.detail.figure')}
                  </dt>
                  <dd className="text-small text-foreground">
                    {details.figureLabel}
                  </dd>
                </div>
                <div>
                  <dt className="text-micro text-muted">
                    {t('feedback.detail.shown')}
                  </dt>
                  <dd className="text-small text-foreground">
                    {details.shownValue}
                  </dd>
                </div>
                <div>
                  <dt className="text-micro text-muted">
                    {t('feedback.detail.expectedValue')}
                  </dt>
                  <dd className="text-small text-foreground">
                    {details.expectedValue}
                  </dd>
                </div>
              </dl>
            )}

            <Section title={t('feedback.detail.whatHappened')}>
              {ticket.friendlySummary ?? ticket.description}
            </Section>

            {ticket.expectedBehaviour && (
              <Section title={t('feedback.detail.expected')}>
                {ticket.expectedBehaviour}
              </Section>
            )}

            {ticket.stepsToReproduce && (
              <Section title={t('feedback.detail.steps')}>
                {ticket.stepsToReproduce}
              </Section>
            )}

            {submissionAttachments.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-small font-semibold text-foreground">
                  {t('feedback.detail.attachments')}
                </h2>
                <ul className="flex flex-wrap gap-2">
                  {submissionAttachments.map((file) => (
                    <li key={file.id}>
                      <ThreadAttachment file={file} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ticket.capturedContext && (
              <CapturedContextPanel context={ticket.capturedContext} />
            )}
          </Card.Body>
        </Card>

        {closed && ticket.resolutionNote && (
          <Card>
            <Card.Body className="space-y-3">
              <h2 className="text-h3 text-foreground">
                {t('feedback.detail.resolution')}
              </h2>
              <p className="whitespace-pre-wrap text-body text-foreground">
                {ticket.resolutionNote}
              </p>
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  onPress={() => setReopenOpen(true)}
                  startIcon={<RotateCcw className="size-4" />}
                >
                  {t('feedback.detail.notFixed')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        <Card>
          <Card.Body>
            <TicketThread
              comments={comments}
              attachments={attachments}
              onReply={reply}
              canReply
            />
          </Card.Body>
        </Card>
      </div>

      <Modal
        isOpen={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title={t('feedback.detail.reopenTitle')}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onPress={() => setReopenOpen(false)}
              isDisabled={reopening}
            >
              {t('feedback.form.cancel')}
            </Button>
            <Button onPress={reopen} isLoading={reopening}>
              {t('feedback.detail.reopenConfirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-body text-foreground">
            {t('feedback.detail.reopenBody')}
          </p>
          <Textarea
            value={reopenReason}
            onChange={(event) => setReopenReason(event.target.value)}
            placeholder={t('feedback.detail.reopenPlaceholder')}
            aria-label={t('feedback.detail.reopenPlaceholder')}
            rows={3}
          />
        </div>
      </Modal>
    </DashboardLayout>
  );
}

/**
 * Which tone the "what happens next" banner takes.
 *
 * Alert has no neutral tone, and mapping every closed state to one colour
 * would put "fixed" and "we won't do this" in the same box — they are
 * different news and should not read the same.
 */
function statusAlertTone(status: string): 'info' | 'success' | 'warning' {
  if (status === 'resolved') return 'success';
  if (status === 'wont_do') return 'warning';
  return 'info';
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <h2 className="text-small font-semibold text-foreground">{title}</h2>
      <p className="whitespace-pre-wrap text-body text-muted">{children}</p>
    </div>
  );
}
